import React, { useCallback, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { AutomationBuilderV2 } from '@frontend/app/components/automationV2/AutomationBuilderV2';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Button } from '@frontend/shadcn/components/ui/button';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { useGetOfferByIdQuery } from '../hooks';

interface EmailAutomationBuilderContainerProps {
  baseUri: string;
}

interface RouteParams {
  offerId: string;
  templateId: string;
  automationId: string;
}

export const EmailAutomationBuilderContainer: React.FC<EmailAutomationBuilderContainerProps> = ({
  baseUri
}) => {
  const { offerId, templateId } = useParams<RouteParams>();
  const searchParams = new URLSearchParams(window.location.search);
  const automationId = searchParams.get('automationId');
  const history = useHistory();
  const offerQuery = useGetOfferByIdQuery({
    variables: {
      id: Number(offerId),
    },
  });
  const { offer } = offerQuery?.data || {};

  // Convert offerId string to number
  const offerIdNumber = parseInt(offerId, 10);

  // Validate route parameters
  const hasValidParams = offerId && templateId && !isNaN(offerIdNumber) && offerIdNumber > 0;

  // Redirect to offer details if parameters are invalid
  useEffect(() => {
    if (!hasValidParams) {
      console.error('Invalid route parameters:', { offerId, templateId, offerIdNumber });
      history.replace(`${baseUri}/offers/${offerId || 'invalid'}/details`);
    }
  }, [hasValidParams, history, baseUri, offerId, templateId, offerIdNumber]);

  // Navigation handlers
  const handleSave = useCallback(() => {
    history.push(`${baseUri}/offers/${offerId}/settings?tab=automation`);
  }, [history, baseUri, offerId]);

  const handleCancel = useCallback(() => {
    history.push(`${baseUri}/offers/${offerId}/details`);
  }, [history, baseUri, offerId]);

  const handleGoBack = useCallback(() => {
    history.push(`${baseUri}/offers/${offerId}/details`);
  }, [history, baseUri, offerId]);

  // Show error state for invalid parameters
  if (!hasValidParams) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Invalid Parameters</h2>
            <p className="text-gray-600 mb-4">
              The automation builder could not be loaded due to invalid or missing parameters.
            </p>
            <Button onClick={handleGoBack} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back to Offer Details
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get offer name dynamically, similar to OfferSettingsCustomTitle
  const offerName = offer?.name || '';

  // Generate breadcrumbs dynamically - matching OfferSettingsCustomTitle pattern exactly
  const breadcrumbs = [
    {
      label: 'Sales Tracking',
      onClick: () => history.push(`${baseUri}/dashboard`), // Navigate to offers list
    },
    {
      label: offerName, // Dynamic offer name like OfferSettingsCustomTitle
      onClick: () => history.push(`${baseUri}/offers/${offerId}/details`), // Navigate to offer details
    },
    {
      label: 'Edit Offer',
      onClick: () => history.push(`${baseUri}/offers/${offerId}/settings`), // Navigate to offer settings
    },
    {
      label: 'Automations',
      onClick: () => history.push(`${baseUri}/offers/${offerId}/settings?tab=automation`), // Navigate to automations tab
    },
  ];

  return (
    <div className="h-screen">
      <AutomationBuilderV2
        projectId={offerIdNumber} // Map offerId to projectId
        templateId={templateId}
        onSave={handleSave}
        onCancel={handleCancel}
        breadcrumbs={breadcrumbs}
        currentPage="Add to Offer"
        automationId={automationId}
        isStaOffer
        redirectUrl={`${baseUri}/offers/${offerId}/settings?tab=automation`}
      />
    </div>
  );
};
